import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Heart, Smartphone, Clock, Shield, CheckCircle2, AlertTriangle, Pill, FileText, Activity } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PatientHistory = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(null);
  const [history, setHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('gp');

  useEffect(() => {
    fetchPageAndHistory();
  }, [pageId]);

  const fetchPageAndHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('Fetching page:', pageId);
      const pageResponse = await axios.get(`${API_URL}/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Page data:', pageResponse.data);
      setPage(pageResponse.data);
      
      const nhsNumber = pageResponse.data.patient?.nhsNumber;
      console.log('Fetching history for URN:', nhsNumber);
      
      if (!nhsNumber) {
        throw new Error('URN not found in page data');
      }
      
      const historyResponse = await axios.get(
        `${API_URL}/patient-history/${nhsNumber}/comprehensive`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('History data:', historyResponse.data);
      setHistory(historyResponse.data);
    } catch (error) {
      console.error('Error fetching patient history:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading patient history from EMIS, MAXIMS & Patient Wallet...</p>
        </div>
      </div>
    );
  }

  if (!page || !history) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Patient history not available</p>
          <p className="text-sm text-gray-500">Page ID: {pageId}</p>
          <p className="text-sm text-gray-500">Page loaded: {page ? 'Yes' : 'No'}</p>
          <p className="text-sm text-gray-500">History loaded: {history ? 'Yes' : 'No'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const gpData = history.sources.gpRecords?.data;
  const hospitalData = history.sources.hospitalRecords?.data;
  const walletData = history.sources.patientWallet;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4">
              <img src="/REACH_LOGO.png" alt="Reach Logo" className="h-10 sm:h-12 w-auto" />
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Patient History</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              {history.timestamp ? new Date(history.timestamp).toLocaleTimeString() : 'Now'}
            </div>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto">
            {['gp', 'hospital', 'wallet'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
                  activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'gp' && <><Building className="w-4 h-4 sm:w-5 sm:h-5" /><span className="hidden sm:inline">GP Records (EMIS)</span><span className="sm:hidden">GP</span></>}
                {tab === 'hospital' && <><Heart className="w-4 h-4 sm:w-5 sm:h-5" /><span className="hidden sm:inline">Hospital (MAXIMS)</span><span className="sm:hidden">Hospital</span></>}
                {tab === 'wallet' && <><Smartphone className="w-4 h-4 sm:w-5 sm:h-5" /><span className="hidden sm:inline">Patient Wallet</span><span className="sm:hidden">Wallet</span><CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 ml-1" /></>}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {activeTab === 'gp' && <GPView data={gpData} />}
        {activeTab === 'hospital' && <HospitalView data={hospitalData} />}
        {activeTab === 'wallet' && <WalletView data={walletData} />}
      </main>
    </div>
  );
};

const GPView = ({ data }) => {
  if (!data) return <div className="text-center py-12 text-gray-600">No GP records available</div>;

  return (
    <div className="space-y-6">
      {/* GP Practice Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-blue-600" />
          GP Practice Information
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Registered GP</p>
            <p className="font-medium text-gray-900">{data.registeredGP}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Practice</p>
            <p className="font-medium text-gray-900">{data.gpPractice}</p>
          </div>
          {data.registrationDate && (
            <div>
              <p className="text-sm text-gray-600">Registered Since</p>
              <p className="font-medium text-gray-900">{data.registrationDate}</p>
            </div>
          )}
        </div>
      </div>

      {data.allergies?.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />ALLERGIES & ADVERSE REACTIONS
          </h2>
          {data.allergies.map((a, i) => (
            <div key={i} className="bg-white p-4 rounded-lg border border-red-200 mb-2">
              <p className="font-bold text-red-900 text-lg">{a.allergen}</p>
              <p className="text-sm text-gray-700">{a.reaction}</p>
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">{a.severity}</span>
            </div>
          ))}
        </div>
      )}

      {data.medications?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Pill className="w-5 h-5 text-green-600" />Current Medications
          </h2>
          {data.medications.map((m, i) => (
            <div key={i} className="border-l-4 border-green-500 bg-gray-50 p-4 rounded mb-3">
              <p className="font-semibold">{m.name} {m.dose}</p>
              <p className="text-sm text-gray-600">{m.frequency} • {m.indication}</p>
            </div>
          ))}
        </div>
      )}

      {data.diagnoses?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />Active Diagnoses
          </h2>
          {data.diagnoses.map((d, i) => (
            <div key={i} className="border-l-4 border-purple-500 bg-gray-50 p-4 rounded mb-3">
              <p className="font-semibold">{d.description}</p>
              <p className="text-sm text-gray-600">Code: {d.code} • {d.severity}</p>
              <p className="text-xs text-gray-500 mt-1">Recorded: {d.dateRecorded}</p>
            </div>
          ))}
        </div>
      )}

      {data.appointments?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />Recent Appointments
          </h2>
          {data.appointments.map((apt, i) => (
            <div key={i} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded mb-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-900">{apt.reason}</p>
                  <p className="text-sm text-gray-600">{apt.clinician} • {apt.type}</p>
                  <p className="text-sm text-gray-500 mt-1">Outcome: {apt.outcome}</p>
                </div>
                <p className="text-sm text-gray-500">{apt.date}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.recentTests?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-600" />Recent Test Results
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.recentTests.map((test, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm text-gray-900">{test.date}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{test.test}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{test.result}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{test.range}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        test.status === 'Normal' ? 'bg-green-100 text-green-800' :
                        test.status === 'Borderline' ? 'bg-yellow-100 text-yellow-800' :
                        test.status === 'Therapeutic' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {test.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const HospitalView = ({ data }) => {
  if (!data?.admissions?.length) return <div className="text-center py-12 text-gray-600">No hospital records</div>;

  const current = data.admissions.find(a => a.status === 'Current Inpatient');
  const previous = data.admissions.filter(a => a.status !== 'Current Inpatient');

  return (
    <div className="space-y-6">
      {current?.clerkingEntry && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5" />CURRENT ADMISSION - Clerking Entry
          </h2>
          <div className="bg-white rounded-lg p-6 space-y-4">
            <div>
              <h3 className="font-bold text-gray-900">Presenting Complaint</h3>
              <p className="bg-gray-50 p-3 rounded mt-2">{current.clerkingEntry.presentingComplaint}</p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">History</h3>
              <p className="bg-gray-50 p-3 rounded mt-2 whitespace-pre-line">{current.clerkingEntry.historyOfPresentingComplaint}</p>
            </div>
            {current.clerkingEntry.concerns?.length > 0 && (
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                <h3 className="font-semibold text-orange-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />Admitting Doctor's Concerns
                </h3>
                <ul className="list-disc list-inside mt-2">
                  {current.clerkingEntry.concerns.map((c, i) => <li key={i} className="text-orange-900">{c}</li>)}
                </ul>
              </div>
            )}
            {current.clerkingEntry.examination && (
              <div>
                <h3 className="font-bold text-gray-900">Examination Findings</h3>
                <div className="bg-gray-50 p-4 rounded mt-2 space-y-3">
                  <p className="text-gray-700"><span className="font-medium">General:</span> {current.clerkingEntry.examination.general}</p>
                  {current.clerkingEntry.examination.vitals && (
                    <div>
                      <p className="font-medium text-gray-900 mb-2">Vital Signs:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(current.clerkingEntry.examination.vitals).map(([key, value]) => (
                          <div key={key} className="bg-white p-3 rounded border border-gray-200">
                            <p className="text-xs text-gray-600 uppercase">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                            <p className="font-medium text-gray-900">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {current.clerkingEntry.examination.cardiovascular && (
                    <p className="text-gray-700"><span className="font-medium">CVS:</span> {current.clerkingEntry.examination.cardiovascular}</p>
                  )}
                  {current.clerkingEntry.examination.respiratory && (
                    <p className="text-gray-700"><span className="font-medium">Resp:</span> {current.clerkingEntry.examination.respiratory}</p>
                  )}
                </div>
              </div>
            )}
            <div>
              <h3 className="font-bold text-gray-900">Impression & Plan</h3>
              <div className="bg-blue-50 p-4 rounded mt-2">
                <p className="font-medium text-blue-900 mb-2">{current.clerkingEntry.impression}</p>
                <p className="text-gray-700 whitespace-pre-line">{current.clerkingEntry.plan}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {previous.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Previous Admissions</h2>
          <div className="space-y-4">
            {previous.map((adm, i) => (
              <div key={i} className="border-l-4 border-gray-300 bg-gray-50 p-4 rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900">{adm.specialty}</p>
                    <p className="text-sm text-gray-600">{adm.admittingConsultant}</p>
                    {adm.dischargeSummary && (
                      <div className="mt-2 text-sm">
                        <p className="text-gray-700"><span className="font-medium">Diagnosis:</span> {adm.dischargeSummary.diagnosis}</p>
                        <p className="text-gray-700"><span className="font-medium">Procedure:</span> {adm.dischargeSummary.procedure}</p>
                        <p className="text-gray-700"><span className="font-medium">Outcome:</span> {adm.dischargeSummary.outcome}</p>
                      </div>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p>{new Date(adm.admissionDate).toLocaleDateString()}</p>
                    {adm.dischargeDate && (
                      <p className="text-xs">to {new Date(adm.dischargeDate).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const WalletView = ({ data }) => {
  if (!data?.verifiableCredentials) return <div className="text-center py-12 text-gray-600">No wallet data</div>;

  const creds = data.verifiableCredentials;

  return (
    <div className="space-y-6">
      {data.consent && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">Patient Consent Granted</p>
              <p className="text-sm text-green-700">Approved at {new Date(data.consent.timestamp).toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {creds.healthGoals && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Patient's Health Goals</h2>
          <ul className="list-disc list-inside space-y-1">
            {creds.healthGoals.data.goals.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
          <div className="bg-blue-50 p-4 rounded mt-4">
            <p><span className="font-medium">Communication:</span> {creds.healthGoals.data.preferences.communicationStyle}</p>
            <p><span className="font-medium">Involvement:</span> {creds.healthGoals.data.preferences.involvementLevel}</p>
          </div>
        </div>
      )}

      {creds.wearableData && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />Wearable Device Data
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {creds.wearableData.data.heartRate && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Heart Rate (7d avg)</p>
                <p className="text-2xl font-bold text-purple-900">{creds.wearableData.data.heartRate.average7Days} bpm</p>
              </div>
            )}
            {creds.wearableData.data.activity && (
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Steps (7d avg)</p>
                <p className="text-2xl font-bold text-green-900">{creds.wearableData.data.activity.steps7DayAvg}</p>
              </div>
            )}
            {creds.wearableData.data.sleep && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Sleep (7d avg)</p>
                <p className="text-2xl font-bold text-blue-900">{creds.wearableData.data.sleep.average7Nights}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {creds.familyHistory && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Family History</h2>
          {creds.familyHistory.data.conditions.map((c, i) => (
            <div key={i} className="border-l-4 border-orange-500 bg-gray-50 p-4 rounded mb-3">
              <p className="font-semibold">{c.relation}: {c.condition}</p>
              <p className="text-sm text-gray-600">Age {c.ageAtDiagnosis} • {c.outcome}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientHistory;
